package ai.flowstorm.studio.ui.design

import ai.flowstorm.common.state.Stateful
import ai.flowstorm.common.state.StatefulValue
import ai.flowstorm.common.ui.DeferredModal
import ai.flowstorm.studio.model.Alternatives
import ai.flowstorm.studio.resources.AlternativeGeneratorResource
import io.kvision.event.clickFlow
import io.kvision.form.text.TextArea
import io.kvision.html.Button
import io.kvision.html.ButtonStyle
import io.kvision.html.button
import io.kvision.html.span
import io.kvision.i18n.I18n
import io.kvision.i18n.I18n.tr
import io.kvision.modal.ModalSize
import io.kvision.panel.hPanel
import io.kvision.state.bind
import io.kvision.utils.em
import io.kvision.utils.px
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.first

class AlternativesModal(promptText: String) : DeferredModal<List<String>>(caption = tr("Alternatives"), size = ModalSize.XLARGE),
    Stateful<AlternativesModal.State> by StatefulValue(State()) {

    data class State(
        val loading: Boolean = false
    )

    private var generated = listOf<String>()
    private val mainScope = MainScope()

    private var prompt = TextArea {
        label = tr("Prompt")
        value = promptText

        bind(this@AlternativesModal) {
            disabled = it.loading
        }
    }

    private var inputText = TextArea {
        label = tr("Examples")

        bind(this@AlternativesModal) {
            disabled = it.loading
        }
    }

    private var outputText = TextArea {
        label = tr("Alternatives")

        bind(this@AlternativesModal) {
            disabled = it.loading
        }
    }

    private val cancelButton = Button(I18n.gettext("Cancel"), style = ButtonStyle.SECONDARY).apply {
        onClick { cancel() }
    }

    private val useButton: Button = button(tr("Use")) {
        bind(this@AlternativesModal) {
            disabled = it.loading
        }
    }.apply {
        onClick {
            mainScope.launch {
                AlternativeGeneratorResource.saveAlternatives(
                    Alternatives(
                        prompt = prompt.value ?: "",
                        source = inputText.value?.lines() ?: listOf(),
                        generated = generated,
                        modified = outputText.value?.lines() ?: listOf()
                    )
                )
            }
        }
    }

    private val getAlternatives = button(tr("Get Alternatives")) {
        bind(inputText) {
            disabled = inputText.value?.isEmpty() ?: true || state.loading
        }
        bind(this@AlternativesModal) {
            disabled = inputText.value?.isEmpty() ?: true || state.loading
        }
    }


    init {
        hPanel(className = "alternatives-modal") {
            spacing = 4
            padding = 4.px
            height = 5.em

            add(prompt)
        }
        hPanel(className = "alternatives-modal") {
            spacing = 4
            padding = 4.px

            add(inputText)
            add(outputText)
        }
        span {
            padding = 4.px
            content = tr("Loading ...")
            bind(this@AlternativesModal) {
                visible = it.loading
            }
        }

        addButton(getAlternatives)
        addButton(useButton)
        addButton(cancelButton)
    }

    fun cancel() {
        hide()
    }

    suspend fun open(input: List<String>): List<String> = coroutineScope {
        inputText.value = input.joinToString("\n")
        show()
        deferred = CompletableDeferred(coroutineContext[Job])


        val bgJob = launch {
            getAlternatives.clickFlow.collect {
                newState { copy(loading = true) }
                generated = AlternativeGeneratorResource.generateAlternatives(
                    Alternatives(prompt=prompt.value ?: "", source=inputText.value?.lines() ?: listOf())
                ).generated
                outputText.value = generated.joinToString("\n")
                newState { copy(loading = false) }
            }
        }

        launch {
            useButton.clickFlow.first()
            deferred.complete(outputText.value!!.lines())
            bgJob.cancel()
            hide()
        }

        deferred.await()
    }

    override suspend fun open(): List<String> = coroutineScope {
        error("not supported")
    }
}
